import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/ui/Button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/Badge';
import { useAuth } from '../context/AuthContext';
import {
  User,
  Settings2,
  Bell,
  ShieldCheck,
  CheckCircle,
  Monitor,
  Clock,
  Globe,
  CalendarDays,
  List,
  Mail,
  BellRing,
  Activity,
  FileText,
  Laptop,
} from 'lucide-react';
import { cn } from '../lib/utils';

// ─── Constants ──────────────────────────────────────────────────────

const STORAGE_KEY = 'aitryx-user-preferences';

const TIMEZONE_OPTIONS = [
  { value: 'Pacific/Auckland', label: '(UTC+12:00) Auckland, Wellington' },
  { value: 'Australia/Sydney', label: '(UTC+11:00) Sydney, Melbourne' },
  { value: 'Australia/Brisbane', label: '(UTC+10:00) Brisbane' },
  { value: 'Australia/Adelaide', label: '(UTC+10:30) Adelaide' },
  { value: 'Australia/Darwin', label: '(UTC+09:30) Darwin' },
  { value: 'Australia/Perth', label: '(UTC+08:00) Perth' },
  { value: 'Asia/Tokyo', label: '(UTC+09:00) Tokyo, Osaka' },
  { value: 'Asia/Shanghai', label: '(UTC+08:00) Beijing, Shanghai' },
  { value: 'Asia/Singapore', label: '(UTC+08:00) Singapore' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) Mumbai, Kolkata' },
  { value: 'Asia/Dubai', label: '(UTC+04:00) Dubai, Abu Dhabi' },
  { value: 'Europe/London', label: '(UTC+00:00) London, Dublin' },
  { value: 'Europe/Paris', label: '(UTC+01:00) Paris, Berlin' },
  { value: 'Europe/Helsinki', label: '(UTC+02:00) Helsinki, Bucharest' },
  { value: 'America/New_York', label: '(UTC-05:00) New York, Toronto' },
  { value: 'America/Chicago', label: '(UTC-06:00) Chicago, Houston' },
  { value: 'America/Denver', label: '(UTC-07:00) Denver, Phoenix' },
  { value: 'America/Los_Angeles', label: '(UTC-08:00) Los Angeles, Seattle' },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2026)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2026)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-12-31)' },
];

const ITEMS_PER_PAGE_OPTIONS = [
  { value: 10, label: '10 items' },
  { value: 25, label: '25 items' },
  { value: 50, label: '50 items' },
];

// ─── Types ──────────────────────────────────────────────────────────

interface UserPreferences {
  timezone: string;
  dateFormat: string;
  itemsPerPage: number;
  notifications: {
    emailNewCustomerActivity: boolean;
    emailSyncAlerts: boolean;
    emailWeeklySummary: boolean;
    inAppNotifications: boolean;
  };
}

const DEFAULT_PREFERENCES: UserPreferences = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney',
  dateFormat: 'DD/MM/YYYY',
  itemsPerPage: 10,
  notifications: {
    emailNewCustomerActivity: true,
    emailSyncAlerts: true,
    emailWeeklySummary: false,
    inAppNotifications: true,
  },
};

// ─── Zod Schema ─────────────────────────────────────────────────────

const preferencesSchema = z.object({
  timezone: z.string().min(1, 'Timezone is required'),
  dateFormat: z.string().min(1, 'Date format is required'),
  itemsPerPage: z.number().min(10).max(50),
});

type PreferencesFormData = z.infer<typeof preferencesSchema>;

// ─── Helpers ────────────────────────────────────────────────────────

function loadPreferences(): UserPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    }
  } catch {
    // ignore parse errors
  }
  return DEFAULT_PREFERENCES;
}

function savePreferences(prefs: UserPreferences): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

function getSessionInfo() {
  const now = new Date();
  const browserInfo = navigator.userAgent;
  let osName = 'Unknown';
  if (browserInfo.includes('Mac')) osName = 'macOS';
  else if (browserInfo.includes('Windows')) osName = 'Windows';
  else if (browserInfo.includes('Linux')) osName = 'Linux';

  let browser = 'Unknown';
  if (browserInfo.includes('Chrome') && !browserInfo.includes('Edg')) browser = 'Chrome';
  else if (browserInfo.includes('Safari') && !browserInfo.includes('Chrome')) browser = 'Safari';
  else if (browserInfo.includes('Firefox')) browser = 'Firefox';
  else if (browserInfo.includes('Edg')) browser = 'Microsoft Edge';

  return {
    os: osName,
    browser,
    loginTime: now.toISOString(),
    location: 'Current Session',
  };
}

// ─── Main Component ─────────────────────────────────────────────────

export default function UserSettingsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { account, partner, partnerAuthContext, isPartnerAdmin } = useAuth();

  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<
    'profile' | 'preferences' | 'notifications' | 'security'
  >((tabFromUrl as 'profile' | 'preferences' | 'notifications' | 'security') || 'profile');

  const [preferences, setPreferences] = useState<UserPreferences>(loadPreferences);
  const [success, setSuccess] = useState<string | null>(null);

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Auto-dismiss success
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Preferences form
  const prefForm = useForm<PreferencesFormData>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: {
      timezone: preferences.timezone,
      dateFormat: preferences.dateFormat,
      itemsPerPage: preferences.itemsPerPage,
    },
  });

  const handleSavePreferences = (data: PreferencesFormData) => {
    const updated: UserPreferences = {
      ...preferences,
      timezone: data.timezone,
      dateFormat: data.dateFormat,
      itemsPerPage: data.itemsPerPage,
    };
    setPreferences(updated);
    savePreferences(updated);
    setSuccess('Preferences saved successfully');
  };

  const handleToggleNotification = useCallback(
    (key: keyof UserPreferences['notifications']) => {
      setPreferences((prev) => {
        const updated = {
          ...prev,
          notifications: {
            ...prev.notifications,
            [key]: !prev.notifications[key],
          },
        };
        savePreferences(updated);
        return updated;
      });
      setSuccess('Notification preferences updated');
    },
    []
  );

  // Derive user info from auth context
  const currentUser = partnerAuthContext?.currentUser;
  const displayName =
    currentUser?.firstName && currentUser?.lastName
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : account?.name || 'User';
  const email = currentUser?.email || account?.username || '';
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const sessionInfo = getSessionInfo();

  const tabs = [
    { id: 'profile' as const, name: 'Profile', icon: User },
    { id: 'preferences' as const, name: 'Preferences', icon: Settings2 },
    { id: 'notifications' as const, name: 'Notifications', icon: Bell },
    { id: 'security' as const, name: 'Security', icon: ShieldCheck },
  ];

  return (
    <div className="flex h-full bg-slate-50">
      <div className="flex w-full">
        {/* Secondary Sidebar - Duralux style */}
        <div className="w-[260px] flex-shrink-0 bg-white border-r border-slate-200">
          <div className="sticky top-0">
            {/* Header */}
            <div className="h-14 px-6 flex items-center border-b border-slate-200">
              <h4 className="text-base font-bold text-slate-900">My Account</h4>
            </div>

            {/* Navigation */}
            <div className="py-3 px-3">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all text-[13px] font-semibold',
                        isActive
                          ? 'bg-orange-50 text-orange-700'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-4 w-4 shrink-0',
                          isActive ? 'text-orange-600' : 'text-slate-400'
                        )}
                      />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6 max-w-[1200px]">
            {/* Success Message */}
            {success && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span className="text-sm text-emerald-800 flex-1">{success}</span>
                  <button
                    onClick={() => setSuccess(null)}
                    className="text-emerald-600 hover:text-emerald-800 transition-colors text-xl leading-none"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}

            {/* ─── Profile Tab ─────────────────────────────────────────── */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* Profile Card */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Your Profile</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Your account information from Azure AD
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start gap-6">
                      {/* Avatar */}
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg flex-shrink-0">
                        {initials}
                      </div>
                      <div className="flex-1 space-y-4">
                        <div>
                          <h2 className="text-xl font-semibold text-slate-900">{displayName}</h2>
                          <p className="text-sm text-slate-500 mt-0.5">{email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={isPartnerAdmin ? 'default' : 'secondary'}
                          >
                            {isPartnerAdmin ? 'Administrator' : 'Staff Member'}
                          </Badge>
                          {partner && (
                            <Badge variant="outline">{partner.partnerName}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Details */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Account Details</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Information synced from your identity provider
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <label className="text-xs text-slate-500">Display Name</label>
                        <p className="font-semibold text-sm text-slate-900">{displayName}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <label className="text-xs text-slate-500">Email Address</label>
                        <p className="font-semibold text-sm text-slate-900">{email}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <label className="text-xs text-slate-500">Role</label>
                        <p className="font-semibold text-sm text-slate-900">
                          {isPartnerAdmin ? 'Partner Administrator' : 'Partner Staff'}
                        </p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <label className="text-xs text-slate-500">Partner Organization</label>
                        <p className="font-semibold text-sm text-slate-900">
                          {partner?.partnerName || 'N/A'}
                        </p>
                      </div>
                      {currentUser?.partnerUserId && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <label className="text-xs text-slate-500">User ID</label>
                          <p className="font-semibold text-sm text-slate-900 font-mono">
                            {currentUser.partnerUserId}
                          </p>
                        </div>
                      )}
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <label className="text-xs text-slate-500">Account Status</label>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          <span className="text-sm font-semibold text-emerald-700">
                            {currentUser?.status || 'Active'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ─── Preferences Tab ─────────────────────────────────────── */}
            {activeTab === 'preferences' && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Preferences</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Customize your experience with display and regional settings
                  </p>
                </div>
                <div className="p-6">
                  <form
                    onSubmit={prefForm.handleSubmit(handleSavePreferences)}
                    className="space-y-6"
                  >
                    {/* Timezone */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="timezone"
                        className="flex items-center gap-2"
                      >
                        <Globe className="h-4 w-4 text-slate-400" />
                        Timezone
                      </Label>
                      <select
                        id="timezone"
                        {...prefForm.register('timezone')}
                        className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
                      >
                        {TIMEZONE_OPTIONS.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </select>
                      {prefForm.formState.errors.timezone && (
                        <p className="text-xs text-rose-600">
                          {prefForm.formState.errors.timezone.message}
                        </p>
                      )}
                    </div>

                    {/* Date Format */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="dateFormat"
                        className="flex items-center gap-2"
                      >
                        <CalendarDays className="h-4 w-4 text-slate-400" />
                        Date Format
                      </Label>
                      <select
                        id="dateFormat"
                        {...prefForm.register('dateFormat')}
                        className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
                      >
                        {DATE_FORMAT_OPTIONS.map((fmt) => (
                          <option key={fmt.value} value={fmt.value}>
                            {fmt.label}
                          </option>
                        ))}
                      </select>
                      {prefForm.formState.errors.dateFormat && (
                        <p className="text-xs text-rose-600">
                          {prefForm.formState.errors.dateFormat.message}
                        </p>
                      )}
                    </div>

                    {/* Items Per Page */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="itemsPerPage"
                        className="flex items-center gap-2"
                      >
                        <List className="h-4 w-4 text-slate-400" />
                        Items Per Page Default
                      </Label>
                      <select
                        id="itemsPerPage"
                        value={prefForm.watch('itemsPerPage')}
                        onChange={(e) => prefForm.setValue('itemsPerPage', Number(e.target.value))}
                        className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
                      >
                        {ITEMS_PER_PAGE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {prefForm.formState.errors.itemsPerPage && (
                        <p className="text-xs text-rose-600">
                          {prefForm.formState.errors.itemsPerPage.message}
                        </p>
                      )}
                    </div>

                    <div className="pt-4 border-t border-slate-200">
                      <Button type="submit">Save Preferences</Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* ─── Notifications Tab ───────────────────────────────────── */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                {/* Email Notifications */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-slate-400" />
                      <div>
                        <h3 className="font-semibold text-slate-900">Email Notifications</h3>
                        <p className="text-sm text-slate-500 mt-0.5">
                          Choose which email notifications you receive
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    <NotificationToggle
                      icon={<Activity className="h-4 w-4 text-orange-500" />}
                      title="New Customer Activity"
                      description="Get notified when a new customer is added or when customer activity occurs"
                      checked={preferences.notifications.emailNewCustomerActivity}
                      onToggle={() => handleToggleNotification('emailNewCustomerActivity')}
                    />
                    <NotificationToggle
                      icon={<BellRing className="h-4 w-4 text-amber-500" />}
                      title="Sync Alerts"
                      description="Receive alerts when product syncs fail or complete with warnings"
                      checked={preferences.notifications.emailSyncAlerts}
                      onToggle={() => handleToggleNotification('emailSyncAlerts')}
                    />
                    <NotificationToggle
                      icon={<FileText className="h-4 w-4 text-sky-500" />}
                      title="Weekly Summary"
                      description="Receive a weekly digest of partner activity and metrics"
                      checked={preferences.notifications.emailWeeklySummary}
                      onToggle={() => handleToggleNotification('emailWeeklySummary')}
                    />
                  </div>
                </div>

                {/* In-App Notifications */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <Bell className="h-5 w-5 text-slate-400" />
                      <div>
                        <h3 className="font-semibold text-slate-900">In-App Notifications</h3>
                        <p className="text-sm text-slate-500 mt-0.5">
                          Control notifications within the application
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    <NotificationToggle
                      icon={<Monitor className="h-4 w-4 text-violet-500" />}
                      title="In-App Notifications"
                      description="Show real-time notifications in the notification bell"
                      checked={preferences.notifications.inAppNotifications}
                      onToggle={() => handleToggleNotification('inAppNotifications')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ─── Security Tab ────────────────────────────────────────── */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                {/* Current Session */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Current Session</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Information about your current active session
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Laptop className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-slate-900">
                            {sessionInfo.browser} on {sessionInfo.os}
                          </h4>
                          <Badge variant="success" size="sm">
                            Current
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Active now • This device</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Login Information */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Login Information</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Details about your authentication
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-4 w-4 text-slate-400" />
                          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                            Last Login
                          </label>
                        </div>
                        <p className="text-sm font-semibold text-slate-900">
                          {currentUser?.lastLoginAt
                            ? new Date(currentUser.lastLoginAt).toLocaleString()
                            : 'Current session'}
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-2 mb-2">
                          <ShieldCheck className="h-4 w-4 text-slate-400" />
                          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                            Authentication Method
                          </label>
                        </div>
                        <p className="text-sm font-semibold text-slate-900">
                          Azure Active Directory (SSO)
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Globe className="h-4 w-4 text-slate-400" />
                          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                            Identity Provider
                          </label>
                        </div>
                        <p className="text-sm font-semibold text-slate-900">
                          Microsoft Entra ID
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                            Account Email
                          </label>
                        </div>
                        <p className="text-sm font-semibold text-slate-900">{email}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Active Sessions */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Active Sessions</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Devices where you are currently signed in
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <Laptop className="h-5 w-5 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-slate-900">
                              {sessionInfo.browser} on {sessionInfo.os}
                            </h4>
                            <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              Active now
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Started:{' '}
                            {new Date(sessionInfo.loginTime).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-4">
                      Session management is handled by Azure AD. To sign out of all devices, use
                      your Microsoft account settings.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function NotificationToggle({
  icon,
  title,
  description,
  checked,
  onToggle,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="px-6 py-4 flex items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex-shrink-0">{icon}</div>
        <div>
          <h4 className="text-sm font-medium text-slate-900">{title}</h4>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onToggle}
        className={cn(
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 flex-shrink-0',
          checked ? 'bg-orange-600' : 'bg-slate-200'
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out',
            checked ? 'translate-x-6' : 'translate-x-1'
          )}
        />
      </button>
    </div>
  );
}
